import { createContext, useState, useEffect } from "react";
import { Alert, Snackbar, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ALERT_SEVERITY_ENUM } from "../helpers/DialogHelpers";
import jwtDecode from "jwt-decode";

// Create a Context object
// When React renders a component that subscribes to this Context object
// it will read the current context value from the closest matching Provider
const AuthContext = createContext({});

// Returns true if the stored token is missing or has expired.
// The token comes from the API after an Azure AD login and lives a full
// workday — there is no refresh flow anymore, the user simply signs in again
// (usually silently, since the Microsoft session is still active).
const isExpired = (tokens) => {
  try {
    return jwtDecode(tokens.accessToken).exp * 1000 <= Date.now();
  }
  catch {
    return true;
  }
}

export const AuthProvider = ({ children }) => {
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState();
  const [alertSeverity, setAlertSeverity] = useState();

  // Get values from localStorage and update the auth state.
  // If there is no valid localStorage item, the auth state is set to null (user not authenticated).
  let [auth, setAuth] = useState(() => {
    const stored = localStorage.getItem('authTokens');
    if (!stored) return null;

    const tokens = JSON.parse(stored);
    if (isExpired(tokens)) {
      localStorage.removeItem('authTokens');
      return null;
    }
    return tokens;
  });

  const closeAlert = () => {
    setAlert(false);
    setAlertMessage("");
    setAlertSeverity(ALERT_SEVERITY_ENUM.INFO);
  }

  const action = (
    <>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={closeAlert}
      >
        <Close />
      </IconButton>
    </>
  );

  // Check the token expiry every minute: warn when the session is about to
  // end and log out automatically when the token has expired.
  useEffect(() => {
    if (!auth) return;

    const fifteenMinutes = 900000; // 15 min in ms

    const checkExpiry = () => {
      const expiresAt = jwtDecode(auth.accessToken).exp * 1000;

      if (expiresAt <= Date.now()) {
        setAuth(null);
        localStorage.removeItem('authTokens');
        setAlert(true);
        setAlertMessage("Din session har gått ut, logga in igen för att fortsätta.");
        setAlertSeverity(ALERT_SEVERITY_ENUM.ERROR);
      }
      else if (expiresAt - Date.now() <= fifteenMinutes) {
        setAlert(true);
        setAlertMessage("Din session går snart ut. Logga in igen för att fortsätta arbeta.");
        setAlertSeverity(ALERT_SEVERITY_ENUM.WARNING);
      }
    }

    checkExpiry();
    const intervalId = setInterval(checkExpiry, 60000);

    return () => { clearInterval(intervalId) }

  }, [auth])

  return (
    // the Provider allows consuming components to subscribe to context changes.
    // Pass the auth prop to consuming components that are descendants of this provider.
    // All consumers that are descendants of this provider will re-render whenever the providers prop changes.
    <>
      <Snackbar open={alert}>
        <Alert severity={alertSeverity} action={action}>
          {alertMessage}
        </Alert>
      </Snackbar>
      <AuthContext.Provider value={{ auth, setAuth }} >
        {children}
      </AuthContext.Provider>
    </>
  )
}

export default AuthContext;
